import type { TypographyProps } from '@mui/material'
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography, styled } from '@mui/material'
import {
  type AdminModel,
  applianceData,
  applianceDataBySku,
  cabinetData,
  cabinetDataBySku,
  createAdminRecipe,
} from 'jms-domain'
import { partData, partDataBySku } from 'jms-domain/src/data/parts'
import { type ReactNode, useEffect, useMemo, useRef } from 'react'

import { GenericDimensionInput } from '@salsita-ai/platform-project-sdk/react'

import {
  getDealerPriceInstanceName,
  getPackageDepthInstanceName,
  getPackageHeightInstanceName,
  getPackageWeightInstanceName,
  getPackageWidthInstanceName,
  getRetailPriceInstanceName,
} from '../../entity-components/admin/product'
import { adminInterface } from '../../systems/admin-interface'

const Grid = styled('div')(() => ({}))
const Cell = styled(Box)({
  'tbody tr:last-child :where(td, th)': { border: 0 },

  'input[type="text"]': {
    textAlign: 'end',
  },
})

const Heading = styled((props: TypographyProps) => <Typography component="h3" variant="h6" {...props} />)(
  ({ theme: { palette, spacing } }) => ({
    color: palette.text.secondary,
    marginBlockStart: spacing(5),
  })
)

interface ProductTableProps {
  children: ReactNode
}

const ProductTable = ({ children }: ProductTableProps) => {
  return (
    <Table sx={{ width: '100%' }}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ width: '60ch' }}>Name</TableCell>
          <TableCell sx={{ width: '25ch' }}>Dealer price</TableCell>
          <TableCell sx={{ width: '25ch' }}>UMRP</TableCell>
          <TableCell sx={{ width: '18ch' }}>Package width</TableCell>
          <TableCell sx={{ width: '18ch' }}>Package height</TableCell>
          <TableCell sx={{ width: '18ch' }}>Package depth</TableCell>
          <TableCell sx={{ width: '20ch' }}>Package weight</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>{children}</TableBody>
    </Table>
  )
}

interface ProductRowProps {
  entityId: AdminModel.PricingItem['id']
  sku: string
  label: ReactNode
}

const ProductRow = ({ entityId, sku, label }: ProductRowProps) => {
  return (
    <TableRow>
      <TableCell>{label}</TableCell>
      <TableCell>
        <GenericDimensionInput entityId={entityId} instanceName={getDealerPriceInstanceName(sku)} />
      </TableCell>
      <TableCell>
        <GenericDimensionInput entityId={entityId} instanceName={getRetailPriceInstanceName(sku)} />
      </TableCell>
      <TableCell>
        <GenericDimensionInput entityId={entityId} instanceName={getPackageWidthInstanceName(sku)} />
      </TableCell>
      <TableCell>
        <GenericDimensionInput entityId={entityId} instanceName={getPackageHeightInstanceName(sku)} />
      </TableCell>
      <TableCell>
        <GenericDimensionInput entityId={entityId} instanceName={getPackageDepthInstanceName(sku)} />
      </TableCell>
      <TableCell>
        <GenericDimensionInput entityId={entityId} instanceName={getPackageWeightInstanceName(sku)} />
      </TableCell>
    </TableRow>
  )
}

export const ProductPage = () => {
  const { domain, storeSystem } = adminInterface.useDomain()
  const state = adminInterface.useCommittedState()
  const $rootEntity = domain.getRoot$Entity(state)
  const $cabinets = $rootEntity.$cabinets.filter(c => !!cabinetDataBySku[c.sku])
  const $parts = $rootEntity.$parts.filter(p => !!partDataBySku[p.sku])
  const $appliances = $rootEntity.$appliances.filter(a => !!applianceDataBySku[a.sku])

  const importRef = useRef<boolean>(false)

  const newCabinets: AdminModel.Cabinet[] = useMemo(() => {
    return Object.values(cabinetData)
      .filter(cabinet => $cabinets.every($cabinet => $cabinet.sku !== cabinet.sku))
      .map((cabinet, index) =>
        createAdminRecipe({
          id: `Cabinet-${index}` as AdminModel.Cabinet['id'],
          type: 'Cabinet',
          dealerPrice: 0,
          retailPrice: 0,
          sku: cabinet.sku,
          packageDepth: 0,
          packageHeight: 0,
          packageWidth: 0,
          packageWeight: 0,
        })
      )
  }, [$cabinets])

  const newAppliances: AdminModel.Appliance[] = useMemo(() => {
    return Object.values(applianceData)
      .filter(appliance => $appliances.every($appliance => $appliance.sku !== appliance.sku))
      .map((appliance, index) =>
        createAdminRecipe({
          id: `Appliance-${index}` as AdminModel.Appliance['id'],
          type: 'Appliance',
          dealerPrice: 0,
          retailPrice: 0,
          sku: appliance.sku,
          packageDepth: 0,
          packageHeight: 0,
          packageWidth: 0,
          packageWeight: 0,
        })
      )
  }, [$appliances])

  const newParts: AdminModel.Part[] = useMemo(() => {
    return Object.values(partData)
      .filter(part => $parts.every($part => $part.sku !== part.sku))
      .map((part, index) =>
        createAdminRecipe({
          id: `Part-${index}` as AdminModel.Part['id'],
          type: 'Part',
          dealerPrice: 0,
          retailPrice: 0,
          sku: part.sku,
          packageDepth: 0,
          packageHeight: 0,
          packageWidth: 0,
          packageWeight: 0,
        })
      )
  }, [$parts])

  const oldCabinets: AdminModel.Cabinet[] = useMemo(() => {
    return $rootEntity.$cabinets.filter(cabinet => !cabinetDataBySku[cabinet.sku])
  }, [$rootEntity.$cabinets])

  const oldAppliances: AdminModel.Appliance[] = useMemo(() => {
    return $rootEntity.$appliances.filter(appliance => !applianceDataBySku[appliance.sku])
  }, [$rootEntity.$appliances])

  const oldParts: AdminModel.Part[] = useMemo(() => {
    return $rootEntity.$parts.filter(part => !partDataBySku[part.sku])
  }, [$rootEntity.$parts])

  const { create: createCabinets } = adminInterface.useCookedRecipes(newCabinets, $rootEntity.id)
  const { create: createAppliances } = adminInterface.useCookedRecipes(newAppliances, $rootEntity.id)
  const { create: createParts } = adminInterface.useCookedRecipes(newParts, $rootEntity.id)

  useEffect(() => {
    if (importRef.current) {
      return
    }

    importRef.current = true

    if (newCabinets.length > 0) {
      createCabinets()
    }

    if (newAppliances.length > 0) {
      createAppliances()
    }

    if (newParts.length > 0) {
      createParts()
    }

    const deleteUpdates = [...oldCabinets, ...oldAppliances, ...oldParts].map(entity =>
      adminInterface.updates.EntityDeletion({
        entityId: entity.id,
        updateTag: 'clearOldProduct',
      })
    )

    if (deleteUpdates.length > 0) {
      storeSystem.stageAndCommit(deleteUpdates)
    }
  }, [
    createCabinets,
    createAppliances,
    createParts,
    newCabinets.length,
    newAppliances.length,
    newParts.length,
    oldCabinets,
    oldAppliances,
    oldParts,
    storeSystem,
  ])

  return (
    <>
      <Grid>
        <Cell data-grid-cell="wide">
          <Heading>Cabinets</Heading>
          <ProductTable>
            {$cabinets.map($cabinet => (
              <ProductRow
                key={$cabinet.id}
                entityId={$cabinet.id}
                sku={$cabinet.sku}
                label={
                  <>
                    <Typography variant="body1">{cabinetDataBySku[$cabinet.sku]?.name ?? 'Unknown'}</Typography>
                    <Typography variant="body2">{$cabinet.sku}</Typography>
                  </>
                }
              />
            ))}
          </ProductTable>
        </Cell>

        <Cell data-grid-cell="wide">
          <Heading>Parts</Heading>
          <ProductTable>
            {$parts.map($part => (
              <ProductRow
                key={$part.id}
                entityId={$part.id}
                sku={$part.sku}
                label={
                  <>
                    <Typography variant="body1">{partDataBySku[$part.sku]?.name ?? 'Unknown'}</Typography>
                    <Typography variant="body2">{$part.sku}</Typography>
                  </>
                }
              />
            ))}
          </ProductTable>
        </Cell>

        <Cell data-grid-cell="wide">
          <Heading>Appliances</Heading>
          <ProductTable>
            {$appliances.map($appliance => (
              <ProductRow
                key={$appliance.id}
                entityId={$appliance.id}
                sku={$appliance.sku}
                label={
                  <>
                    <Typography variant="body1" sx={{ whiteSpace: 'nowrap' }}>
                      {applianceDataBySku[$appliance.sku]?.name ?? 'Unknown'}
                    </Typography>
                    <Typography variant="body2">{$appliance.sku}</Typography>
                  </>
                }
              />
            ))}
          </ProductTable>
        </Cell>
      </Grid>
    </>
  )
}
